// ── 점수 기준표 편집 모달 ─────────────────────────────────────────────────────
//   기준표는 회사별 + 적용 시작월 버전으로 저장된다.
//   기존 월을 그대로 두고 새 적용월로 저장하면 과거 점수는 절대 바뀌지 않는다.
import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';

const inputStyle = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--card)', color: 'var(--text)', fontSize: 13,
};
const label = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, display: 'block' };
const box = { border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12 };

const clone = (v) => JSON.parse(JSON.stringify(v));

export default function ScoreCriteriaModal({ open, onClose, onSaved, companyCode, config, effectiveFromMonth, defaultMonth, showToast }) {
  const [draft, setDraft] = useState(() => (config ? clone(config) : null));
  const [applyMonth, setApplyMonth] = useState(defaultMonth || '');
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);

  const autoItems = useMemo(() => (draft?.items || []).filter((i) => i.mode === 'auto'), [draft]);
  const manualItems = useMemo(() => (draft?.items || []).filter((i) => i.mode === 'manual'), [draft]);
  if (!draft) return null;

  function patchEmployment(field, raw) {
    const value = String(raw).replace(/[^0-9.]/g, '');
    setDraft((prev) => {
      const next = clone(prev);
      if (field === 'minHours') next.employment.fulltimeMinWeeklyHours = value === '' ? '' : Number(value);
      if (field === 'parttimeScale') next.employment.scales.parttime = value === '' ? '' : Number(value);
      return next;
    });
  }

  function patchBand(itemKey, index, pos, raw) {
    const value = String(raw).replace(/[^0-9.]/g, '');
    setDraft((prev) => {
      const next = clone(prev);
      const item = next.items.find((i) => i.key === itemKey);
      if (item) item.bands[index][pos] = value === '' ? '' : Number(value);
      return next;
    });
  }

  function patchManual(itemKey, field, raw) {
    const value = String(raw).replace(/[^0-9.]/g, '');
    setDraft((prev) => {
      const next = clone(prev);
      const item = next.items.find((i) => i.key === itemKey);
      if (item) item[field] = value === '' ? '' : Number(value);
      return next;
    });
  }

  async function handleSave() {
    if (!/^\d{4}-\d{2}$/.test(String(applyMonth))) {
      showToast('적용 시작월을 YYYY-MM 형식으로 입력해 주세요.', 'error');
      return;
    }
    setSaving(true);
    const res = await apiClient.put('/api/admin/tm-score/rules', {
      company_code: companyCode, effective_from_month: applyMonth, config: draft, memo,
    });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '기준표 저장 실패', 'error'); return; }
    showToast(`${applyMonth}부터 적용되는 기준표를 저장했습니다.`, 'success');
    onSaved?.();
    onClose();
  }

  return (
    <Modal
      open={open}
      title="점수 기준 설정"
      icon="📋"
      onClose={() => { if (!saving) onClose(); }}
      width="min(760px, 96vw)"
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: 'pointer', fontWeight: 700 }}>닫기</button>
          <button onClick={handleSave} disabled={saving} style={{ ...inputStyle, cursor: 'pointer', fontWeight: 800, background: '#7c3aed', color: '#fff', border: '1px solid #7c3aed' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}
    >
      <div style={{ ...box, background: 'rgba(124,58,237,0.06)' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <span style={label}>적용 시작월</span>
            <input style={{ ...inputStyle, width: 110 }} type="text" inputMode="numeric" maxLength={7}
              value={applyMonth} placeholder="2026-09"
              onChange={(e) => setApplyMonth(e.target.value.replace(/[^0-9-]/g, '').slice(0, 7))} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <span style={label}>메모(선택)</span>
            <input style={{ ...inputStyle, width: '100%' }} type="text" value={memo}
              onChange={(e) => setMemo(e.target.value)} placeholder="변경 사유" />
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7 }}>
          현재 적용 중인 기준표: <b>{effectiveFromMonth || '-'}</b>부터.
          새 적용월로 저장하면 <b>그 달부터만</b> 바뀌고 이전 달 점수는 그대로 유지됩니다.
        </div>
      </div>

      <div style={box}>
        <div style={{ fontWeight: 800, marginBottom: 10, color: 'var(--text)' }}>근무구분 판정</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <span style={label}>풀타임 기준선 (주 소정근로시간 이상)</span>
            <input style={{ ...inputStyle, width: 90, textAlign: 'right' }} type="text" inputMode="decimal" maxLength={5}
              value={draft.employment.fulltimeMinWeeklyHours}
              onChange={(e) => patchEmployment('minHours', e.target.value)} />
          </div>
          <div>
            <span style={label}>파트타임 배율</span>
            <input style={{ ...inputStyle, width: 90, textAlign: 'right' }} type="text" inputMode="decimal" maxLength={5}
              value={draft.employment.scales.parttime}
              onChange={(e) => patchEmployment('parttimeScale', e.target.value)} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          파트타임은 별도 표 없이 실적을 배율로 나눠 아래 표를 그대로 적용합니다
          (배율 0.7이면 2,800장 = 풀타임 4,000장 취급).
        </div>
      </div>

      {autoItems.map((item) => (
        <div key={item.key} style={box}>
          <div style={{ fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>
            {item.label}
            {item.aboveLast?.mode === 'linear' && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                최고 구간 초과 시 {item.aboveLast.unit}{item.unit || ''}당 +{item.aboveLast.points}점
              </span>
            )}
            {item.maxPoints !== null && item.maxPoints !== undefined && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>· 최대 {item.maxPoints}점</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {item.bands.map((band, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${item.key}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <input style={{ ...inputStyle, width: 66, textAlign: 'right' }} type="text" inputMode="decimal" maxLength={7}
                  value={band[0]} onChange={(e) => patchBand(item.key, idx, 0, e.target.value)} />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.unit}=</span>
                <input style={{ ...inputStyle, width: 52, textAlign: 'right' }} type="text" inputMode="decimal" maxLength={5}
                  value={band[1]} onChange={(e) => patchBand(item.key, idx, 1, e.target.value)} />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>점</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>구간 사이 값은 비례 보간으로 계산됩니다.</div>
        </div>
      ))}

      <div style={box}>
        <div style={{ fontWeight: 800, marginBottom: 8, color: 'var(--text)' }}>직접 입력 항목</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {manualItems.map((item) => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              <div>
                <span style={label}>{item.label} 기본점수</span>
                <input style={{ ...inputStyle, width: 70, textAlign: 'right' }} type="text" inputMode="decimal" maxLength={5}
                  value={item.defaultPoints} onChange={(e) => patchManual(item.key, 'defaultPoints', e.target.value)} />
              </div>
              <div>
                <span style={label}>최대</span>
                <input style={{ ...inputStyle, width: 70, textAlign: 'right' }} type="text" inputMode="decimal" maxLength={5}
                  value={item.maxPoints} onChange={(e) => patchManual(item.key, 'maxPoints', e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
